import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, fmt, fmtDate, toDateInput, getUser, toUsd } from '../api';
import { Modal, Field, Input, useToast, Loading, Empty, Eyebrow } from '../ui';
import { Icons } from '../icons';

const CUR_SYM: Record<string, string> = { CNY: '¥', USD: '$', UZS: "so'm" };
const sumsText = (o: Record<string, number>) =>
  Object.keys(o).length === 0 ? '—' : Object.entries(o).map(([c, v]) => `${fmt(v, 2)} ${CUR_SYM[c] || c}`).join(' + ');
/** Aralash valyutani bitta $ ga keltiradi (kurs: 1 USD = X birlik, shuning uchun bo'linadi). */
const sumsUsd = (o: Record<string, number>) => {
  let total = 0;
  for (const [c, v] of Object.entries(o || {})) {
    const u = toUsd(v, c);
    if (u == null) return '';
    total += u;
  }
  return total > 0 ? '≈ $' + fmt(total, 0) : '';
};

export default function Payments() {
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [params] = useSearchParams();
  // Zavodlar sahifasidan «akt sverka» bosilsa — o'sha zavod darrov tanlanadi
  const [supplierId, setSupplierId] = useState(params.get('supplierId') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [recon, setRecon] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [add, setAdd] = useState<any>(null);
  const toast = useToast();
  const nav = useNavigate();
  const user = getUser();
  const canEdit = ['admin', 'zakaz_operator'].includes(user?.role);

  useEffect(() => {
    api.get('/suppliers').then(setSuppliers).catch((e) => { toast(e.message, true); setSuppliers([]); });
    api.get('/currencies').then(setCurrencies).catch(() => setCurrencies([]));
  }, []);

  const load = (sid: string, from = dateFrom, to = dateTo) => {
    if (!sid) { setRecon(null); return; }
    setLoading(true);
    const qs = new URLSearchParams({ supplierId: sid });
    if (from) qs.set('dateFrom', from);
    if (to) qs.set('dateTo', to);
    api.get('/reconciliation?' + qs.toString()).then((r) => { setRecon(r); setLoading(false); }).catch((e) => { toast(e.message, true); setLoading(false); });
  };
  useEffect(() => { load(supplierId); }, [supplierId, dateFrom, dateTo]);

  const savePayment = async () => {
    try {
      await api.post('/payments', { ...add, supplierId: Number(supplierId) });
      toast("To'lov saqlandi"); setAdd(null); load(supplierId);
    } catch (e: any) { toast(e.message, true); }
  };

  const removePayment = async (id: number) => {
    if (!confirm("Bu to'lov o'chirilsinmi?")) return;
    try { await api.del('/payments/' + id); toast("To'lov o'chirildi"); load(supplierId); }
    catch (e: any) { toast(e.message, true); }
  };

  const supplier = suppliers.find((s) => String(s.id) === supplierId);

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">To'lovlar va akt sverka</h1>
          <p className="page-sub">Zavod bilan hisob-kitob: zakazlar (debet) va to'lovlar (kredit)</p>
        </div>
        {canEdit && supplierId && (
          <button className="btn btn-primary" onClick={() => setAdd({ date: toDateInput(new Date()), currency: 'CNY' })}>
            <Icons.plus size={16} /> To'lov kiritish
          </button>
        )}
      </div>

      <div className="filters">
        <select className="input" style={{ width: 280 }} value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
          <option value="">— Zavodni tanlang —</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {supplierId && <>
          <input className="input" style={{ width: 150 }} type="date" title="Sanadan" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <input className="input" style={{ width: 150 }} type="date" title="Sanagacha" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          {(dateFrom || dateTo) && <button className="btn" onClick={() => { setDateFrom(''); setDateTo(''); }}>Tozalash</button>}
        </>}
      </div>

      {!supplierId ? (
        <Empty text="Zavodni tanlang" hint="Akt sverka tanlangan zavod bo'yicha shakllanadi" />
      ) : loading || !recon ? <Loading /> : (
        <>
          <div className="stat-grid">
            <div className="stat"><div className="k">Zakazlar (debet)</div><div className="v" style={{ fontSize: 20 }}>{sumsText(recon.debit)}<div className="muted" style={{ fontSize: 12, fontWeight: 400 }}>{sumsUsd(recon.debit)}</div></div></div>
            <div className="stat ok"><div className="k">To'lovlar (kredit)</div><div className="v" style={{ fontSize: 20 }}>{sumsText(recon.credit)}<div className="muted" style={{ fontSize: 12, fontWeight: 400 }}>{sumsUsd(recon.credit)}</div></div></div>
            <div className={'stat' + (Object.values(recon.balance).some((v: any) => v > 0) ? ' warn' : ' ok')}>
              <div className="k">Saldo (qarzimiz)</div><div className="v" style={{ fontSize: 20 }}>{sumsText(recon.balance)}<div className="muted" style={{ fontSize: 12, fontWeight: 400 }}>{sumsUsd(recon.balance)}</div></div>
            </div>
          </div>

          <Eyebrow title={`Harakatlar — ${supplier?.name || ''}`} desc="sana bo'yicha: zakazlar va to'lovlar" />
          {recon.rows.length === 0 ? <Empty text="Bu zavod bo'yicha harakat yo'q" /> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Sana</th><th>Turi</th><th>Hujjat / izoh</th><th className="num">Debet (zakaz)</th><th className="num">Kredit (to'lov)</th><th></th></tr></thead>
                <tbody>
                  {recon.rows.map((r: any, i: number) => (
                    <tr key={i}>
                      <td>{fmtDate(r.date)}</td>
                      <td>{r.type === 'order'
                        ? <span className="badge b-confirmed">Zakaz</span>
                        : r.type === 'opening'
                          ? <span className="badge b-draft">Saldo</span>
                          : <span className="badge b-received">To'lov</span>}</td>
                      <td>{r.type === 'order'
                        ? <span className="link" style={{ cursor: 'pointer' }} onClick={() => nav('/orders/' + r.id)}>{r.ref}</span>
                        : r.ref}</td>
                      <td className="num">{r.type === 'order' || (r.type === 'opening' && r.isDebit) ? `${fmt(r.amount, 2)} ${CUR_SYM[r.currency] || r.currency}` : ''}</td>
                      <td className="num">{r.type === 'payment' || (r.type === 'opening' && !r.isDebit) ? `${fmt(r.amount, 2)} ${CUR_SYM[r.currency] || r.currency}` : ''}</td>
                      <td>
                        {r.type === 'payment' && user?.role === 'admin' && (
                          <button className="act act-del" title="To'lovni o'chirish" onClick={() => removePayment(r.id)}>
                            <Icons.trash size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {add && (
        <Modal title={`To'lov — ${supplier?.name || ''}`} onClose={() => setAdd(null)}>
          <div className="grid-2">
            <Field label="Sana"><Input type="date" value={add.date} onChange={(e) => setAdd({ ...add, date: e.target.value })} /></Field>
            <Field label="Summa *"><Input type="number" min="0" step="0.01" value={add.amount ?? ''} onChange={(e) => setAdd({ ...add, amount: e.target.value })} /></Field>
            <Field label="Valyuta">
              <select className="input" value={add.currency} onChange={(e) => setAdd({ ...add, currency: e.target.value })}>
                {(currencies.length ? currencies : [{ id: 0, code: 'CNY' }, { id: 1, code: 'USD' }]).map((c: any) => <option key={c.id} value={c.code}>{c.code}</option>)}
              </select>
            </Field>
            <Field label="Usul"><Input placeholder="naqd / o'tkazma / WeChat…" value={add.method || ''} onChange={(e) => setAdd({ ...add, method: e.target.value })} /></Field>
          </div>
          <Field label="Izoh"><Input value={add.note || ''} onChange={(e) => setAdd({ ...add, note: e.target.value })} /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn" onClick={() => setAdd(null)}>Bekor</button>
            <button className="btn btn-primary" onClick={savePayment}>Saqlash</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
